import { useState, useEffect } from 'react';
import { 
  Stethoscope, 
  Mail, 
  Phone, 
  CreditCard, 
  Edit2, 
  Save, 
  X, 
  AlertCircle,
  CheckCircle,
  Crown
} from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MedicoSettings = ({ famigliaId, piano, onSettingsChange }) => {
  const [settings, setSettings] = useState({
    nome_medico: '',
    email_medico: '',
    cellulare_medico: '',
    codice_fiscale_assistito: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);

  const isPremium = piano === 'premium';

  useEffect(() => {
    if (famigliaId && isPremium) {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [famigliaId, isPremium]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/famiglia/${famigliaId}/medico-settings`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setSettings({
            nome_medico: data.settings.nome_medico || '',
            email_medico: data.settings.email_medico || '',
            cellulare_medico: data.settings.cellulare_medico || '',
            codice_fiscale_assistito: data.settings.codice_fiscale_assistito || ''
          });
          setIsConfigured(data.is_configured);
          
          // Se non è configurato, mostra automaticamente in editing
          if (!data.is_configured) {
            setIsEditing(true);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching medico settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    
    // Validazione
    if (!settings.nome_medico.trim()) {
      setError('Inserisci il nome del medico');
      return;
    }
    if (!settings.email_medico.trim() || !settings.email_medico.includes('@')) {
      setError('Inserisci un\'email valida');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/famiglia/${famigliaId}/medico-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setSuccess('Impostazioni salvate con successo!');
          setIsEditing(false);
          setIsConfigured(true);
          if (onSettingsChange) {
            onSettingsChange(settings);
          }
          setTimeout(() => setSuccess(null), 3000);
        }
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Errore nel salvataggio');
      }
    } catch (err) {
      setError('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    fetchSettings();
    setIsEditing(false);
    setError(null);
  };

  // Se non è Premium, mostra upgrade banner
  if (!isPremium) {
    return (
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#2C3E50]">Gestione Ricette Premium</h3>
            <p className="text-sm text-[#64748B]">Funzionalità esclusiva per utenti Premium</p>
          </div>
        </div>
        <p className="text-sm text-[#64748B] mb-4">
          Configura il tuo medico di base e richiedi ricette con un clic. 
          Le email vengono inviate automaticamente allo studio medico.
        </p>
        <Button
          className="bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90"
          onClick={() => window.location.href = '/#prezzi'}
        >
          <Crown className="w-4 h-4 mr-2" />
          Passa a Premium
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#2C3E50]">Anagrafica Medico di Base</h3>
              <p className="text-xs text-[#64748B]">
                {isConfigured ? 'Configurato' : 'Configura per richiedere ricette'}
              </p>
            </div>
          </div>
          
          {!isEditing && isConfigured && (
            <Button
              onClick={() => setIsEditing(true)}
              variant="outline"
              className="text-sm"
              data-testid="edit-medico-btn"
            >
              <Edit2 className="w-4 h-4 mr-1" />
              Modifica
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-5 space-y-4">
        {/* Status Messages */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            {success}
          </div>
        )}

        {/* First time setup notice */}
        {!isConfigured && isEditing && (
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Prima configurazione:</strong> Inserisci i dati del tuo medico di base. 
              Saranno utilizzati per inviare automaticamente le richieste ricette.
            </p>
          </div>
        )}

        {/* Form Fields */}
        <div className="space-y-4">
          {/* Nome Medico */}
          <div>
            <label className="block text-sm font-medium text-[#2C3E50] mb-1.5">
              <Stethoscope className="w-4 h-4 inline mr-1" />
              Nome del Medico
            </label>
            {isEditing ? (
              <input
                type="text"
                value={settings.nome_medico}
                onChange={(e) => setSettings({...settings, nome_medico: e.target.value})}
                placeholder="es. Dott. Mario Rossi"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                data-testid="nome-medico-input"
              />
            ) : (
              <p className="px-4 py-2.5 bg-gray-50 rounded-xl text-[#2C3E50]">
                {settings.nome_medico || <span className="text-gray-400">Non configurato</span>}
              </p>
            )}
          </div>

          {/* Email Medico */}
          <div>
            <label className="block text-sm font-medium text-[#2C3E50] mb-1.5">
              <Mail className="w-4 h-4 inline mr-1" />
              Email dello Studio
            </label>
            {isEditing ? (
              <input
                type="email"
                value={settings.email_medico}
                onChange={(e) => setSettings({...settings, email_medico: e.target.value})}
                placeholder="es. studio.rossi@email.it"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                data-testid="email-medico-input"
              />
            ) : (
              <p className="px-4 py-2.5 bg-gray-50 rounded-xl text-[#2C3E50]">
                {settings.email_medico || <span className="text-gray-400">Non configurato</span>}
              </p>
            )}
          </div>

          {/* Cellulare Medico - DEPRECATED: WhatsApp removed */}
          <div>
            <label className="block text-sm font-medium text-[#2C3E50] mb-1.5">
              <Phone className="w-4 h-4 inline mr-1" />
              Cellulare Medico <span className="text-gray-400 font-normal">(opzionale)</span>
            </label>
            {isEditing ? (
              <input
                type="tel"
                value={settings.cellulare_medico}
                onChange={(e) => setSettings({...settings, cellulare_medico: e.target.value})}
                placeholder="es. +39 333 1234567"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                data-testid="cellulare-medico-input"
              />
            ) : (
              <p className="px-4 py-2.5 bg-gray-50 rounded-xl text-[#2C3E50]">
                {settings.cellulare_medico || <span className="text-gray-400">Non configurato</span>}
              </p>
            )}
            {isEditing && (
              <p className="text-xs text-gray-500 mt-1">
                Per contatti telefonici diretti
              </p>
            )}
          </div>

          {/* Codice Fiscale */}
          <div>
            <label className="block text-sm font-medium text-[#2C3E50] mb-1.5">
              <CreditCard className="w-4 h-4 inline mr-1" />
              Codice Fiscale Assistito
            </label>
            {isEditing ? (
              <input
                type="text"
                value={settings.codice_fiscale_assistito}
                onChange={(e) => setSettings({...settings, codice_fiscale_assistito: e.target.value.toUpperCase()})}
                placeholder="es. RSSMRA85M01H501Z"
                maxLength={16}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all uppercase"
                data-testid="cf-assistito-input"
              />
            ) : (
              <p className="px-4 py-2.5 bg-gray-50 rounded-xl text-[#2C3E50] font-mono">
                {settings.codice_fiscale_assistito || <span className="text-gray-400 font-sans">Non configurato</span>}
              </p>
            )}
            {isEditing && (
              <p className="text-xs text-gray-500 mt-1">
                Il medico potrà identificare subito il paziente senza cercare in archivio
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {isEditing && (
          <div className="flex gap-3 pt-4 border-t border-gray-100">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:opacity-90"
              data-testid="save-medico-btn"
            >
              {saving ? (
                <>Salvataggio...</>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salva Impostazioni
                </>
              )}
            </Button>
            {isConfigured && (
              <Button
                onClick={handleCancel}
                variant="outline"
                className="px-6"
              >
                <X className="w-4 h-4 mr-1" />
                Annulla
              </Button>
            )}
          </div>
        )}

        {/* Info Box */}
        {isConfigured && !isEditing && (
          <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200">
            <p className="text-sm text-green-800">
              <CheckCircle className="w-4 h-4 inline mr-1" />
              <strong>Pronto!</strong> Ora puoi richiedere ricette dalla sezione "Farmaci". 
              Il sistema invierà automaticamente la richiesta al tuo medico.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicoSettings;
